import { TFunction } from 'react-i18next';
import * as Yup from 'yup';
import { IRGBColor } from '../../components/developers/sites/ColorModule';
import { IWidgetConfigurationData } from '../../interfaces/merchant';

export enum FORM_FIELDS {
	ID ='id',
	FONT_FAMILY = 'fontFamily',
	FONT_COLOR = 'fontColor',
	BUTTON_COLOR = 'buttonColor',
	LOGO_FILE = 'logoFile',
	VISIBLE = 'isLogoVisible',
	SUCCESS_URL = 'successUrl',
	REJECT_URL = 'rejectUrl',
	CANCEL_URL = 'cancelUrl',
	BUTTON_RADIUS = 'buttonsCornersRadius',
	MODAL_RADIUS = 'modalCornersRadius',
	INTERACTION_TYPE = 'consentFlowConfiguration'
}

export enum FORM_FIELDS_NAMES {
	FONT_FAMILY = 'COLOR_MODULE.LABELS.FONT_FAMILY',
	FONT_COLOR = 'COLOR_MODULE.LABELS.FONT_COLOR',
	BUTTON_COLOR = 'COLOR_MODULE.LABELS.BUTTON_COLOR',
	LOGO_FILE = 'COLOR_MODULE.LABELS.LOGO_FILE',
	SUCCESS_URL = 'COLOR_MODULE.LABELS.SUCCESS_URL',
	REJECT_URL = 'COLOR_MODULE.LABELS.REJECT_URL',
	CANCEL_URL = 'COLOR_MODULE.LABELS.CANCEL_URL',
	BUTTON_RADIUS = 'COLOR_MODULE.LABELS.BUTTON_RADIUS',
	MODAL_RADIUS = 'COLOR_MODULE.LABELS.MODAL_RADIUS',
	INTERACTION_TYPE = 'COLOR_MODULE.LABELS.INTERACTION_TYPE',
}

export const getWidgetSchema = (t: TFunction): any => Yup.object().shape({
	[FORM_FIELDS.FONT_FAMILY]: Yup.string()
		.required(t('COLOR_MODULE.VALIDATION.FONT_FAMILY_REQUIRED')),
	[FORM_FIELDS.FONT_COLOR]: Yup.string()
		.test('light-color', t('COLOR_MODULE.VALIDATION.FONT_COLOR_TO_LIGHT'), (hex) => {
			const value = hex.split('#');

			const hexToRgb = (hex): IRGBColor => {
				const bigint = parseInt(hex, 16);
				const r = (bigint >> 16) & 255;
				const g = (bigint >> 8) & 255;
				const b = bigint & 255;

				return { r, g, b };
			};

			const { r, g, b } = hexToRgb(value[1]);
			return (r + g + b) <= 600;
		})
		.required(t('COLOR_MODULE.VALIDATION.FONT_COLOR_REQUIRED')),
	[FORM_FIELDS.BUTTON_COLOR]: Yup.string()
		.required(t('COLOR_MODULE.VALIDATION.BUTTON_COLOR_REQUIRED')),
	[FORM_FIELDS.SUCCESS_URL]: Yup.string()
		.matches(
			/^(?:([a-z0-9+.-]+):\/\/)(?:\S+(?::\S*)?@)?(?:(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*\.?)(?::\d{2,5})?(?:[/?#]\S*)?$/,
			t('COLOR_MODULE.VALIDATION.VALID_URL')
		),
		// .url(t('COLOR_MODULE.VALIDATION.VALID_URL')),
	[FORM_FIELDS.REJECT_URL]: Yup.string()
		.matches(
			/^(?:([a-z0-9+.-]+):\/\/)(?:\S+(?::\S*)?@)?(?:(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*\.?)(?::\d{2,5})?(?:[/?#]\S*)?$/,
			t('COLOR_MODULE.VALIDATION.VALID_URL')
		),
		// .url(t('COLOR_MODULE.VALIDATION.VALID_URL')),
	[FORM_FIELDS.CANCEL_URL]: Yup.string()
		.matches(
			/^(?:([a-z0-9+.-]+):\/\/)(?:\S+(?::\S*)?@)?(?:(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*\.?)(?::\d{2,5})?(?:[/?#]\S*)?$/,
			t('COLOR_MODULE.VALIDATION.VALID_URL')
		)
		// .url(t('COLOR_MODULE.VALIDATION.VALID_URL'))
});

export type IWidget = {
	[FORM_FIELDS.ID]?: string,
	[FORM_FIELDS.FONT_COLOR]?: string,
	[FORM_FIELDS.BUTTON_COLOR]?: string,
	[FORM_FIELDS.FONT_FAMILY]?: string,
	[FORM_FIELDS.LOGO_FILE]?: string,
	[FORM_FIELDS.VISIBLE]?: boolean,
	[FORM_FIELDS.SUCCESS_URL]?: string,
	[FORM_FIELDS.REJECT_URL]?: string,
	[FORM_FIELDS.CANCEL_URL]?: string,
	[FORM_FIELDS.BUTTON_RADIUS]?: number,
	[FORM_FIELDS.MODAL_RADIUS]?: number,
	[FORM_FIELDS.INTERACTION_TYPE]?: string | number,

};

export const initialValues: IWidget = {
	[FORM_FIELDS.FONT_COLOR]: '',
	[FORM_FIELDS.BUTTON_COLOR]: '',
	[FORM_FIELDS.FONT_FAMILY]: '',
	[FORM_FIELDS.LOGO_FILE]: '',
	[FORM_FIELDS.VISIBLE]: false,
	[FORM_FIELDS.SUCCESS_URL]: '',
	[FORM_FIELDS.REJECT_URL]: '',
	[FORM_FIELDS.CANCEL_URL]: '',
	[FORM_FIELDS.BUTTON_RADIUS]: 0,
	[FORM_FIELDS.MODAL_RADIUS]: 0,
	[FORM_FIELDS.INTERACTION_TYPE]: 1,
};

export const getFormattedFormFieldsForAdmin = (formikValues): IWidgetConfigurationData => ({
	id: formikValues?.id,
	fontFamily: formikValues?.fontFamily,
	fontColor: formikValues?.fontColor,
	buttonColor: formikValues?.buttonColor,
	logoFile: formikValues.logoFile ? formikValues.logoFile : null,
	isLogoVisible: formikValues?.isLogoVisible,
	successUrl: formikValues?.successUrl,
	rejectUrl: formikValues?.rejectUrl,
	cancelUrl: formikValues?.cancelUrl,
	buttonsCornersRadius: formikValues?.buttonsCornersRadius,
	modalCornersRadius: formikValues?.modalCornersRadius,
	consentFlowConfiguration: formikValues?.consentFlowConfiguration || null,
});

export const getFormattedFormFieldsForMerchant = (formikValues): any => ({
	id: formikValues?.id,
	isLogoVisible: formikValues?.isLogoVisible,
	logoFile: formikValues?.logoFile ? formikValues.logoFile : null,
	successUrl: formikValues?.successUrl,
	rejectUrl: formikValues?.rejectUrl,
	cancelUrl: formikValues?.cancelUrl,
});

export const convertToInitial = (data): any => ({
	[FORM_FIELDS.ID]: data?.id ? data.id : '',
	[FORM_FIELDS.FONT_FAMILY]: data?.fontFamily ? data.fontFamily : 'Arial',
	[FORM_FIELDS.FONT_COLOR]: data?.fontColor ? data.fontColor : '#000000',
	[FORM_FIELDS.BUTTON_COLOR]: data?.buttonColor ? data.buttonColor : '#000000',
	[FORM_FIELDS.VISIBLE]: data?.isLogoVisible ? data.isLogoVisible : false,
	[FORM_FIELDS.LOGO_FILE]: data?.logoFile,
	[FORM_FIELDS.SUCCESS_URL]: data?.successUrl ? data.successUrl : '',
	[FORM_FIELDS.REJECT_URL]: data?.rejectUrl ? data.rejectUrl : '',
	[FORM_FIELDS.CANCEL_URL]: data?.cancelUrl ? data.cancelUrl : '',
	[FORM_FIELDS.BUTTON_RADIUS]: data?.buttonsCornersRadius ? data.buttonsCornersRadius : 0,
	[FORM_FIELDS.MODAL_RADIUS]: data?.modalCornersRadius ? data.modalCornersRadius : 0,
	[FORM_FIELDS.INTERACTION_TYPE]: data?.consentFlowConfiguration ? data.consentFlowConfiguration : 1,
});


export interface IWidgetLinkInputs {
	key: string;
	fieldLabel: string;
	fieldName: string;
	tooltipText: string;
}
